import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { ms, pct01, perHour } from "../lib/format";

// Screen 4 — A/B comparison dashboards (Design 2.7 #4).
//
// The operator picks TWO releases (defaulting to whatever is assigned to the
// exp-A / exp-B channels) and reads their configMetrics side by side: labeled
// count, FP/FN rate, avg latency, wake frequency — with a Δ (B − A) column and
// a per-metric "winner" hint (lower FP/FN/latency/wake is better; higher
// correct rate is better). All of the goodness logic is computed server-side
// in configMetricsCompare so the table here is purely presentational.
export function Comparison() {
  const releases = useQuery(api.releases.listReleases, {});
  const defaults = useQuery(api.releases.experimentDefaults, {});
  const [a, setA] = useState<string>("");
  const [b, setB] = useState<string>("");
  // Once we've seeded the pickers from the exp-A/exp-B channel defaults we stop
  // overriding the operator's manual choices.
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (seeded || defaults === undefined || releases === undefined) return;
    // Only seed with ids that actually exist in the current release list.
    const exists = (id: string | null) =>
      !!id && releases.some((r) => r._id === id);
    if (exists(defaults.expA)) setA(defaults.expA as string);
    if (exists(defaults.expB)) setB(defaults.expB as string);
    setSeeded(true);
  }, [seeded, defaults, releases]);

  return (
    <div className="screen">
      <header className="screen-head">
        <h1>Comparison dashboards</h1>
        <p className="screen-sub">
          Per-release FP / FN / latency / wake-frequency. Defaults to the
          releases on the exp-A and exp-B channels; the Δ column flags whether
          B moved each metric in the better or worse direction.
        </p>
      </header>

      {releases === undefined ? (
        <Skeleton />
      ) : releases.length === 0 ? (
        <EmptyState
          title="No releases to compare"
          body="Create releases and let detections accumulate, then compare here."
        />
      ) : (
        <>
          <div className="compare-pickers">
            <ReleasePicker
              label="Config A"
              value={a}
              onChange={(v) => {
                setSeeded(true);
                setA(v);
              }}
              releases={releases}
            />
            <span className="vs">vs</span>
            <ReleasePicker
              label="Config B"
              value={b}
              onChange={(v) => {
                setSeeded(true);
                setB(v);
              }}
              releases={releases}
            />
          </div>

          {!a || !b ? (
            <EmptyState
              title="Pick two configs"
              body="Select a release for both A and B to see the side-by-side deltas."
            />
          ) : a === b ? (
            <EmptyState
              title="Same release selected"
              body="Pick two different releases to compare them."
            />
          ) : (
            <CompareBody a={a} b={b} releases={releases} />
          )}
        </>
      )}
    </div>
  );
}

// The two-column header cards + the delta table. Reads the single
// configMetricsCompare query (one round-trip, both sides + verdicts).
function CompareBody({
  a,
  b,
  releases,
}: {
  a: string;
  b: string;
  releases: ReleaseRow[];
}) {
  const cmp = useQuery(api.detections.configMetricsCompare, {
    releaseIdA: a as Id<"releases">,
    releaseIdB: b as Id<"releases">,
  });

  const labelFor = (id: string) => {
    const r = releases.find((x) => x._id === id);
    return r ? `${r.version} · ${r.channel}` : id;
  };

  if (cmp === undefined) {
    return (
      <div className="compare-table-wrap">
        <Skeleton />
      </div>
    );
  }

  const noLabels = cmp.a.labeled === 0 && cmp.b.labeled === 0;

  return (
    <>
      <div className="compare-cols">
        <MetricsCard side="A" title={labelFor(a)} m={cmp.a} />
        <MetricsCard side="B" title={labelFor(b)} m={cmp.b} />
      </div>

      {noLabels ? (
        <div className="compare-note">
          No labeled detections on either config yet — rates are blank. Label
          some detections on the Detections screen to compare FP / FN / correct
          rates. Counts, latency and wake-frequency below are already live.
        </div>
      ) : null}

      <DeltaTable
        verdicts={cmp.verdicts}
        labelA={labelFor(a)}
        labelB={labelFor(b)}
      />
    </>
  );
}

type Verdict = {
  key: string;
  label: string;
  unit: "rate" | "ms" | "perHour" | "count";
  direction: "up" | "down" | "neutral";
  a: number | null;
  b: number | null;
  delta: number | null;
  winner: "A" | "B" | "tie" | null;
};

function fmtVal(v: number | null, unit: Verdict["unit"]): string {
  if (v == null) return "—";
  switch (unit) {
    case "rate":
      return pct01(v);
    case "ms":
      return ms(v);
    case "perHour":
      return perHour(v);
    case "count":
      return String(v);
  }
}

// Format the Δ (B − A) in the metric's own unit, signed. Lower-is-better
// metrics still show the raw signed delta; the tone/winner conveys good/bad.
function fmtDelta(v: Verdict): string {
  if (v.delta == null) return "—";
  if (v.delta === 0) return "0";
  const sign = v.delta > 0 ? "+" : "−";
  const mag = Math.abs(v.delta);
  switch (v.unit) {
    case "rate":
      return `${sign}${(mag * 100).toFixed(1)} pts`;
    case "ms":
      return `${sign}${Math.round(mag)} ms`;
    case "perHour":
      return `${sign}${mag >= 100 ? Math.round(mag) : mag.toFixed(1)}/h`;
    case "count":
      return `${sign}${mag}`;
  }
}

function DeltaTable({
  verdicts,
  labelA,
  labelB,
}: {
  verdicts: Verdict[];
  labelA: string;
  labelB: string;
}) {
  return (
    <div className="compare-table-wrap">
      <table className="compare-table">
        <thead>
          <tr>
            <th>Metric</th>
            <th className="col-a">A · {labelA}</th>
            <th className="col-b">B · {labelB}</th>
            <th className="col-delta">Δ (B − A)</th>
            <th className="col-winner">Winner</th>
          </tr>
        </thead>
        <tbody>
          {verdicts.map((v) => {
            // tone of the delta cell: good when B is the winner, bad when A is,
            // muted for ties / neutral / undeterminable.
            const tone =
              v.winner === "B" ? "good" : v.winner === "A" ? "bad" : undefined;
            return (
              <tr key={v.key}>
                <td className="cell-metric">{v.label}</td>
                <td className="cell-num">{fmtVal(v.a, v.unit)}</td>
                <td className="cell-num">{fmtVal(v.b, v.unit)}</td>
                <td
                  className={`cell-num cell-delta ${
                    tone ? `delta-${tone}` : ""
                  }`}
                >
                  {fmtDelta(v)}
                </td>
                <td className="cell-winner">
                  <WinnerBadge winner={v.winner} direction={v.direction} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="compare-legend">
        Lower is better for FP / FN / wrong-tier / latency / wake-frequency;
        higher is better for correct rate. Counts are informational.
      </div>
    </div>
  );
}

function WinnerBadge({
  winner,
  direction,
}: {
  winner: Verdict["winner"];
  direction: Verdict["direction"];
}) {
  if (direction === "neutral") return <span className="win-neutral">—</span>;
  if (winner == null)
    return <span className="win-neutral" title="Not enough data">n/a</span>;
  if (winner === "tie") return <span className="win-tie">tie</span>;
  return <span className={`win-badge win-${winner}`}>{winner}</span>;
}

// Minimal shape we read from listReleases (full return type lives in the
// generated API; this keeps the picker prop typing simple + stable).
type ReleaseRow = {
  _id: Id<"releases">;
  version: string;
  channel: string;
  status: string;
};

function ReleasePicker({
  label,
  value,
  onChange,
  releases,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  releases: ReleaseRow[];
}) {
  return (
    <label className="field compact">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— select release —</option>
        {releases.map((r) => (
          <option key={r._id} value={r._id}>
            {r.version} · {r.channel} · {r.status}
          </option>
        ))}
      </select>
    </label>
  );
}

type SideMetrics = {
  total: number;
  labeled: number;
  correct: number;
  falsePos: number;
  falseNeg: number;
  wrongTier: number;
  avgLatencyMs: number | null;
  wakeCount: number;
  wakeFreqPerHour: number | null;
  falsePosRate: number | null;
  falseNegRate: number | null;
  correctRate: number | null;
};

function MetricsCard({
  side,
  title,
  m,
}: {
  side: string;
  title: string;
  m: SideMetrics;
}) {
  return (
    <div className="metrics-card">
      <div className="metrics-side">{side}</div>
      <div className="metrics-title">{title}</div>
      <div className="kpi-row">
        <Kpi label="Detections" value={String(m.total)} />
        <Kpi label="Labeled" value={String(m.labeled)} />
        <Kpi label="Wake freq" value={perHour(m.wakeFreqPerHour)} />
      </div>
      <div className="kpi-row">
        <Kpi
          label="False positive"
          value={pct01(m.falsePosRate)}
          sub={`${m.falsePos}`}
          tone="bad"
        />
        <Kpi
          label="False negative"
          value={pct01(m.falseNegRate)}
          sub={`${m.falseNeg}`}
          tone="bad"
        />
        <Kpi
          label="Correct"
          value={pct01(m.correctRate)}
          sub={`${m.correct}`}
          tone="good"
        />
      </div>
      <div className="kpi-row">
        <Kpi label="Wrong tier" value={String(m.wrongTier)} />
        <Kpi label="Avg latency" value={ms(m.avgLatencyMs)} />
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "good" | "bad";
}) {
  return (
    <div className={`kpi ${tone ? `kpi-${tone}` : ""}`}>
      <div className="kpi-value">{value}</div>
      <div className="kpi-label">{label}</div>
      {sub && <div className="kpi-sub">{sub} count</div>}
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      <div className="empty-body">{body}</div>
    </div>
  );
}

function Skeleton() {
  return <div className="skeleton">Loading…</div>;
}
